import { useEffect, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../utils/cn'

interface ModalProps {
  /** Accepts both 'open' and 'isOpen' for convenience */
  open?: boolean
  isOpen?: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
}

export function Modal({
  open,
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  className,
}: ModalProps) {
  const isVisible = open ?? isOpen ?? false

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (!isVisible) return
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isVisible, handleEscape])

  if (!isVisible) return null

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div
        className={cn(
          'relative z-[1] max-h-[90vh] w-full overflow-y-auto',
          sizeStyles[size],
          'bg-white/90 dark:bg-slate-900/90',
          'backdrop-blur-[20px]',
          'border border-slate-200/25 dark:border-slate-700/20',
          'rounded-[28px] shadow-2xl',
          '!text-slate-900 dark:!text-white',
          'animate-[fadeInUp_0.3s_cubic-bezier(0.16,1,0.3,1)_both]',
          className,
        )}
      >
        {title && (
          <div className="flex items-center justify-between border-b border-slate-200/20 px-6 py-5 dark:border-slate-700/15">
            <h3 className="text-lg font-extrabold tracking-tight !text-slate-900 dark:!text-white">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-xl !text-slate-600 transition-colors hover:bg-slate-100/60 dark:!text-slate-400 dark:hover:bg-white/5"
            >
              ✕
            </button>
          </div>
        )}
        <div className="p-6 !text-slate-900 dark:!text-white">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
